import { DestroyRef, inject, Injectable, NgZone, Type } from "@angular/core";
import { createAnimation } from "@ionic/angular/standalone";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ModalController } from "@ionic/angular/standalone";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import { ScreenSaverComponent, ScreenSaverConfig } from "./screen-saver.component";

// inject so we never need to add it to the app.scss or forget to copy it
const SCREEN_SAVER_MODAL_STYLES = `
.screen-saver-modal,
.md .screen-saver-modal {
  --width: 100% !important;
  --min-height: 100% !important;
  --max-height: 100% !important;
  --background: transparent !important;
  --backdrop-opacity: 0 !important;
  --border-radius: 0;
  backdrop-filter: blur(15px);
}
`;

export interface ScreenSaverServiceConfig {
  leftComponent: Type<unknown>;
  rightComponent: Type<unknown>;
  screenSaverConfig?: ScreenSaverConfig;
  cssClass?: string;
  activeOnRoutes?: string[];
  getInactivityDuration: () => number;
  isEnabled: () => boolean;
  shouldNotOpenWhen?: () => boolean | Promise<boolean>;
}

@Injectable({providedIn: 'root'})
export class ScreenSaverService {
  private readonly modalCtrl = inject(ModalController);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private config!: ScreenSaverServiceConfig;
  private inactivityTimeout: any;
  private isScreenSaverOpen = false;
  private isDismissed = false;
  private screenSaverModalRef!: HTMLIonModalElement;
  private stylesInjected = false;
  readonly gestureExcludeSelectors = new Set<string>();

  private readonly userActivityEvents = ["click", "touchstart", "mousemove", "keydown"];
  private readonly onUserActivity = () => {
    if (this.isDismissed) {
      this.isDismissed = false;
    }
    this.resetInactivityTimer();
  };

  init(config: ScreenSaverServiceConfig) {
    this.config = config;
    this.injectModalStyles();
    this.setupActivityListeners();
    this.setupRouterListener();

    this.destroyRef.onDestroy(() => {
      this.clearInactivityTimeout();
      this.removeActivityListeners();
    });
    this.resetInactivityTimer();
  }

  private injectModalStyles() {
    if (this.stylesInjected) return;

    const styleId = "screen-saver-modal-styles";
    if (document.getElementById(styleId)) {
      this.stylesInjected = true;
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = SCREEN_SAVER_MODAL_STYLES;
    document.head.appendChild(style);
    this.stylesInjected = true;
  }

  async show() {
    if (!this.config || this.isScreenSaverOpen) return;

    if (this.config.activeOnRoutes?.length) {
      const isOnActiveRoute = this.config.activeOnRoutes.includes(this.router.url);
      if (!isOnActiveRoute) return;
    }

    if (await this.config.shouldNotOpenWhen?.()) return;

    this.isScreenSaverOpen = true;
    this.screenSaverModalRef = await this.modalCtrl.create({
      component: ScreenSaverComponent,
      cssClass: this.config.cssClass ?? "screen-saver-modal",
      backdropDismiss: false,
      componentProps: {
        leftComponent: this.config.leftComponent,
        rightComponent: this.config.rightComponent,
        config: this.config.screenSaverConfig ?? {},
      },
      enterAnimation: (baseEl: HTMLElement) => {
        const root = baseEl.shadowRoot?.querySelector('.modal-wrapper') ?? baseEl;
        return createAnimation()
          .addElement(root)
          .duration(400)
          .easing('ease-out')
          .fromTo('opacity', '0', '1')
          .fromTo('transform', 'translateY(-100%)', 'translateY(0)');
      },
    });

    await this.screenSaverModalRef.present();
    await this.screenSaverModalRef.onDidDismiss();

    this.isScreenSaverOpen = false;
    this.isDismissed = true;
    this.resetInactivityTimer();
  }

  async dismiss(animate = true) {
    if (this.screenSaverModalRef) {
      if (animate) {
        this.screenSaverModalRef.leaveAnimation = (baseEl: HTMLElement) => {
          const root = baseEl.shadowRoot?.querySelector('.modal-wrapper') ?? baseEl;
          return createAnimation()
            .addElement(root)
            .duration(300)
            .easing('ease-out')
            .fromTo('transform', 'translateY(0)', 'translateY(-100%)');
        };
      } else {
        this.screenSaverModalRef.leaveAnimation = () => createAnimation().duration(0);
      }
      await this.screenSaverModalRef.dismiss();
    }
  }

  addEventGesture(selector: string) {
    this.gestureExcludeSelectors.add(selector);
  }

  removeEventGesture(selector: string) {
    this.gestureExcludeSelectors.delete(selector);
  }

  resetInactivityTimer() {
    this.clearInactivityTimeout();

    if (!this.config || this.isScreenSaverOpen) return;
    if (!this.config.isEnabled()) return;

    if (this.config.activeOnRoutes?.length) {
      const isOnActiveRoute = this.config.activeOnRoutes.includes(this.router.url);
      if (!isOnActiveRoute) return;
    }

    const durationInSeconds = this.config.getInactivityDuration();
    if (durationInSeconds <= 0) return;

    this.ngZone.runOutsideAngular(() => {
      this.inactivityTimeout = setTimeout(() => {
        this.ngZone.run(() => this.show());
      }, durationInSeconds * 1000);
    });
  }

  stop(dismissIfOpen = true) {
    this.clearInactivityTimeout();
    if (dismissIfOpen && this.isScreenSaverOpen && this.screenSaverModalRef) {
      this.screenSaverModalRef.dismiss();
    }
  }

  private setupActivityListeners() {
    this.userActivityEvents.forEach((event) => {
      const options = event === "touchstart" ? { passive: true } : undefined;
      document.addEventListener(event, this.onUserActivity, options);
    });
  }

  private removeActivityListeners() {
    this.userActivityEvents.forEach((event) => {
      document.removeEventListener(event, this.onUserActivity);
    });
  }

  private setupRouterListener() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        if (!this.config?.activeOnRoutes?.length) {
          this.resetInactivityTimer();
          return;
        }

        const isOnActiveRoute = this.config.activeOnRoutes.includes(event.urlAfterRedirects);
        if (isOnActiveRoute) {
          this.resetInactivityTimer();
        } else {
          this.stop();
        }
      });
  }

  private clearInactivityTimeout() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
  }
}
